import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Eye, EyeOff, Loader2, ShieldCheck, TrendingUp, Users,
  BarChart3, LockKeyhole, Zap, ArrowRight, Mail,
} from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import "./LoginFuturistic.css";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) {
    const blocked =
      !["admin", "super_admin"].includes(user.role) &&
      (user.status !== "approved" ||
        user.plan_state === "expired" ||
        user.plan_state === "pending_trial");
    return <Navigate to={blocked ? "/planos" : "/"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
    } else {
      toast.success("Bem-vindo(a) de volta!");
    }
  };

  return (
    <main className="ms-login" data-testid="login-page">
      <div className="ms-login-grid" aria-hidden="true" />
      <div className="ms-login-glow ms-login-glow-one" aria-hidden="true" />
      <div className="ms-login-glow ms-login-glow-two" aria-hidden="true" />

      <section className="ms-login-hero">
        <header className="ms-login-brand">
          <Logo size={52} />
          <div>
            <div className="ms-brand-name">MS Soluções Financeiras</div>
            <div className="ms-brand-subtitle">PAINEL DE EMPRÉSTIMOS</div>
          </div>
          <div className="ms-hero-status"><span /> SISTEMA SEGURO</div>
        </header>

        <div className="ms-hero-copy">
          <div className="ms-eyebrow"><span /> GESTÃO INTELIGENTE</div>
          <h1>Controle total sobre<br />seus <span>empréstimos</span><br />pessoais.</h1>
          <p>Cadastre clientes, acompanhe cobranças, visualize pagamentos e tenha tudo em um painel rápido, seguro e feito para você.</p>

          <div className="ms-feature-row">
            <div className="ms-feature"><span className="ms-feature-icon"><Users /></span><b>Gestão<br />de clientes</b></div>
            <div className="ms-feature"><span className="ms-feature-icon"><BarChart3 /></span><b>Relatórios<br />em tempo real</b></div>
            <div className="ms-feature"><span className="ms-feature-icon"><ShieldCheck /></span><b>Dados<br />protegidos</b></div>
            <div className="ms-feature"><span className="ms-feature-icon"><Zap /></span><b>Interface<br />moderna</b></div>
          </div>
        </div>

        <div className="ms-dashboard-preview" aria-hidden="true">
          <div className="ms-preview-top"><span className="ms-preview-mark">MS</span><span>VISÃO GERAL</span><span className="ms-preview-live"><i /> LIVE</span></div>
          <div className="ms-preview-metrics">
            <div><small>CLIENTES ATIVOS</small><strong>248</strong><em>+12%</em></div>
            <div><small>EMPRÉSTIMOS</small><strong>R$ 125.430</strong><em>+8%</em></div>
            <div><small>EM ATRASO</small><strong>12</strong><em className="ms-negative">-3%</em></div>
          </div>
          <div className="ms-preview-chart"><div className="ms-chart-heading">Evolução de pagamentos <span>ÚLTIMOS 30 DIAS</span></div><div className="ms-chart-bars">{[22,35,28,46,39,57,48,67,54,74,61,82,70,88,63,78,51,69,44,58,36,48,31,42,24,34,19,28].map((h,i)=><i key={i} style={{height:`${h}%`}} />)}</div></div>
          <div className="ms-preview-bottom"><span><i className="ms-dot-cyan"/> Recebimentos em dia</span><span><i className="ms-dot-blue"/> Acompanhamento inteligente</span></div>
        </div>

        <footer className="ms-login-copyright">© 2026 MS Soluções Financeiras · Todos os direitos reservados</footer>
      </section>

      <section className="ms-login-panel">
        <div className="ms-login-panel-top"><span>SEGURANÇA</span><b>•</b><span>RAPIDEZ</span><b>•</b><span>CONTROLE</span><i /></div>
        <form onSubmit={submit} className="ms-auth-card" data-testid="login-form">
          <div className="ms-auth-brand"><Logo size={62} /><strong>MS Soluções Financeiras</strong><span>PAINEL DE EMPRÉSTIMOS</span></div>
          <div className="ms-auth-heading"><h2>Entrar na sua conta</h2><p>Acesse seu painel de gestão de empréstimos.</p></div>

          <div className="ms-auth-field">
            <Label htmlFor="email"><Mail /> E-mail</Label>
            <Input id="email" type="email" placeholder="voce@exemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="ms-auth-input" />
          </div>
          <div className="ms-auth-field">
            <Label htmlFor="password"><LockKeyhole /> Senha</Label>
            <div className="ms-password-wrap">
              <Input id="password" type={showPwd ? "text" : "password"} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="ms-auth-input ms-password-input" />
              <button type="button" onClick={() => setShowPwd((s) => !s)} className="ms-password-toggle" data-testid="login-toggle-password" aria-label="Alternar senha">{showPwd ? <EyeOff /> : <Eye />}</button>
            </div>
          </div>

          {error && <div className="ms-auth-error" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={busy} className="ms-auth-submit" data-testid="login-submit-btn">{busy ? <Loader2 className="animate-spin" /> : <> <span>Entrar</span><ArrowRight /></>}</Button>
          <div className="ms-auth-forgot"><Link to="/forgot-password" data-testid="login-forgot-password-link">Esqueci minha senha</Link></div>
          <div className="ms-auth-divider"><span /> OU <span /></div>
          <p className="ms-auth-register">Ainda não tem um painel? <Link to="/register" data-testid="go-register">Criar conta <ArrowRight /></Link></p>
          <div className="ms-auth-secure"><ShieldCheck /> CONEXÃO PROTEGIDA · SEUS DADOS SÃO PRIVADOS</div>
        </form>
        <div className="ms-panel-version">MS SYSTEM <span>•</span> ACESSO AUTORIZADO</div>
      </section>
    </main>
  );
}
